import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Search, X, Loader2 } from "lucide-react";

export const LocationAutocomplete = ({
  value,
  onChange,
  placeholder = "Search for a location",
  className = "",
  disabled = false,
  label = "Location",
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // OpenRouteService API key (you should move this to environment variables)
  const ORS_API_KEY =
    "eyJvcmciOiI1YjNjZTM1OTc4NTExMTAwMDFjZjYyNDgiLCJpZCI6IjZhOTFkNmIyODAxZTRjODZiMDg4NTViOTkwZmVjYTg4IiwiaCI6Im11cm11cjY0In0=";

  // Debounced search function
  const searchLocations = useCallback(
    async (query) => {
      if (!query || query.length < 3) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://api.openrouteservice.org/geocode/search?api_key=${ORS_API_KEY}&text=${encodeURIComponent(query)}&size=5&boundary.country=IN`
        );

        if (!response.ok) {
          throw new Error("Geocoding failed");
        }

        const data = await response.json();

        const formattedSuggestions = data.features.map((feature) => ({
          id: feature.properties.id,
          label: feature.properties.label,
          name: feature.properties.name,
          locality: feature.properties.locality,
          region: feature.properties.region,
          country: feature.properties.country,
          coordinates: feature.geometry.coordinates,
          confidence: feature.properties.confidence,
        }));

        setSuggestions(formattedSuggestions);
      } catch (error) {
        console.error("Error searching locations:", error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [ORS_API_KEY]
  );

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout for debounced search
    debounceRef.current = setTimeout(() => {
      searchLocations(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.label);
    setSelectedLocation(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);

    // Call onChange with the selected location data
    onChange({
      address: suggestion.label,
      coordinates: suggestion.coordinates,
      locationData: suggestion,
    });
  };

  // Handle input focus
  const handleFocus = () => {
    if (inputValue.length >= 3) {
      setShowSuggestions(true);
    }
  };

  // Handle clear button
  const handleClear = () => {
    setInputValue("");
    setSelectedLocation(null);
    setShowSuggestions(false);
    setSuggestions([]);
    onChange({
      address: "",
      coordinates: null,
      locationData: null,
    });
    inputRef.current?.focus();
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-4 w-4 text-gray-400" />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg 
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
            disabled:bg-gray-50 disabled:text-gray-500 
            ${selectedLocation ? "bg-green-50 border-green-300" : ""}
            ${className}
          `}
        />

        {loading && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          </div>
        )}

        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Location validation indicator */}
      {selectedLocation && (
        <div className="mt-1 flex items-center text-sm text-green-600">
          <MapPin className="h-3 w-3 mr-1" />
          <span>Location validated</span>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {suggestion.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.label}
                  </p>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-gray-400">
                      {suggestion.locality}, {suggestion.region}
                    </span>
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">
                      {Math.round(suggestion.confidence * 100)}% match
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions &&
        suggestions.length === 0 &&
        inputValue.length >= 3 &&
        !loading && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
            <div className="flex items-center text-gray-500">
              <Search className="h-4 w-4 mr-2" />
              <span className="text-sm">
                No locations found for "{inputValue}"
              </span>
            </div>
          </div>
        )}
    </div>
  );
};
